import React, { useEffect, useState } from "react";
import { Check, Loader2, MonitorX, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { fetchContext, readCtx, requestReset, requestSwitch } from "@/lib/roleContext";
import { revokeOtherSessions } from "@/lib/sessionPersistence";

const signOutOthers = async () => {
  try {
    const n = await revokeOtherSessions();
    toast.success(n ? `Signed out ${n} other session${n === 1 ? "" : "s"}` : "No other sessions were signed in");
  } catch (e) { toast.error(e.message); }
};

const Item = ({ a, current }) => (
  <DropdownMenuItem role="menuitemradio" aria-checked={a.active} onSelect={() => !a.active && requestSwitch(a, current)}
    data-testid={`role-switch-item-${a.id}`} className="min-h-[40px] cursor-pointer items-start gap-2 py-2">
    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${a.active ? "text-[#007a9e]" : "invisible"}`} aria-hidden />
    <span className="min-w-0 text-[13px] leading-snug">
      <span className="font-medium">{a.role_label}</span>
      <span className="text-slate-500"> — {a.org_name}</span>
    </span>
  </DropdownMenuItem>
);

// Rendered INSIDE the existing top-right user menu: current context + permitted assignments.
export const RoleSwitchItems = () => {
  const [d, setD] = useState(null);
  useEffect(() => { fetchContext().then(setD).catch(() => setD(null)); }, []);
  const local = readCtx();
  if (!d) return local ? <div className="px-2 py-1.5"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div> : null;
  const cur = d.current || {};
  const demo = d.assignments.filter((a) => a.environment === "demo");
  const prod = [d.home, ...d.assignments.filter((a) => a.environment === "production")];
  const inOwnDemo = cur.environment === "demo" && d.own_workspace && cur.workspace_id === d.own_workspace.id;
  return (
    <div data-testid="role-switch-section">
      <div className="px-2 py-1.5 text-[12px] text-slate-600" data-testid="role-switch-current">
        <span className="text-slate-400">Current: </span>
        <span className="font-semibold text-slate-800">{cur.role_label}</span> · {cur.org_name}
        {cur.environment === "demo" && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-bold text-amber-800">DEMO</span>}
      </div>
      {d.switchable && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">Switch Role</DropdownMenuLabel>
          <div role="group" aria-label="Demo roles">
            {demo.length > 0 && <div className="px-2 pt-1 text-[10px] font-bold tracking-widest text-amber-700">DEMO</div>}
            {demo.map((a) => <Item key={a.id} a={a} current={cur} />)}
          </div>
          <div role="group" aria-label="Production roles">
            <div className="px-2 pt-2 text-[10px] font-bold tracking-widest text-slate-500">PRODUCTION</div>
            {prod.map((a) => <Item key={a.id} a={a} current={cur} />)}
          </div>
          {inOwnDemo && (
            <DropdownMenuItem onSelect={requestReset} data-testid="reset-my-demo-item" className="mt-1 min-h-[40px] cursor-pointer text-amber-800">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset My Demo
            </DropdownMenuItem>
          )}
        </>
      )}
      <DropdownMenuItem onSelect={signOutOthers} data-testid="sign-out-other-sessions-item" className="min-h-[40px] cursor-pointer text-[13px]">
        <MonitorX className="mr-2 h-4 w-4" aria-hidden /> Sign out other sessions
      </DropdownMenuItem>
      <DropdownMenuSeparator />
    </div>
  );
};
